import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { journey } from "src/types";

const initialState: journey.JourneyStateType = {
  cycles: {
    data: [],
    isLoading: false,
    isSuccess: null,
  },
  ftuDocuments: {
    data: [],
    isSuccess: null,
  },
  cycleDocuments: {
    data: {
      documents: [],
      testReports: {
        data: [],
        page: "1",
        isLastPageReached: false,
      },
    },
    isLoading: false,
    isSuccess: null,
  },
  updatePatientVisit: {
    data: [],
    isLoading: false,
    isSuccess: null,
  },
  createCycle: {
    data: [],
    isSuccess: null,
    isLoading: false,
    errors: "",
  },
  getDrugs: {
    data: [],
    isSuccess: null,
    isLoading: false,
  },
};

const JourneySlice = createSlice({
  name: "journey",
  initialState,
  reducers: {
    getCyclesRequest: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.cycles.isSuccess = null;
      state.cycles.isLoading = true;
    },
    getCyclesSuccess: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.cycles.isSuccess = true;
      state.cycles.isLoading = false;
      state.cycles.data = action?.payload;
    },
    getCyclesError: (
      state: journey.JourneyStateType,
      { payload: string }: PayloadAction<string>
    ) => {
      state.cycles.isSuccess = false;
      state.cycles.isLoading = false;
    },

    markAttendanceRequest: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {},
    markAttendanceSuccess: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      let currentCycleIndex: number = state.cycles.data.findIndex(
        (i) => i.patient_visit_id === action.payload.patient_visit_id
      );

      let tempCycles: journey.CycleItemType[] = [...state.cycles.data];
      tempCycles[currentCycleIndex].visit_status = "VNU"; //Attended cycle so status goes 'N' => 'VNU'

      state.cycles.data = tempCycles;
    },
    markAttendanceFailed: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.cycles.isSuccess = false;
      state.cycles.isLoading = false;
    },

    rescheduleCycleRequest: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {},
    rescheduleCycleSuccess: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      let currentCycleIndex: number = state.cycles.data.findIndex(
        (i) => i.patient_visit_id === action.payload.patient_visit_id
      );

      let tempCycles: journey.CycleItemType[] = [...state.cycles.data];
      tempCycles[currentCycleIndex].rescheduled_date = moment(
        action.payload.new_date
      ).format("YYYY-MM-DD HH:mm:ss"); //Attended cycle so status goes 'N' => 'VNU'

      state.cycles.data = tempCycles;
    },
    rescheduleCycleFailed: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.cycles.isSuccess = false;
      state.cycles.isLoading = false;
    },

    getCycleDocumentsRequest: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.cycleDocuments.isSuccess = null;
      state.cycleDocuments.isLoading = true;
    },
    getCycleDocumentsSuccess: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.cycleDocuments.isSuccess = true;
      state.cycleDocuments.isLoading = false;
      state.cycleDocuments.data.documents = action?.payload?.filter(
        (doc: any) => doc.test_name !== "Lab Tests"
      );
      state.cycleDocuments.data.testReports.data = action?.payload?.filter(
        (doc: any) => doc.test_name === "Lab Tests"
      );
      // state.cycleDocuments.data.testReports.isLastPageReached =
      //   (action?.payload?.data ?? []).length <= 5;
      // if ((action?.payload?.data ?? []).length >= 5) {
      //   state.cycleDocuments.data.testReports.page = (
      //     parseInt(state.cycleDocuments.data.testReports.page) + 1
      //   ).toString();
      // }
    },
    getCycleDocumentsError: (
      state: journey.JourneyStateType,
      { payload: string }: PayloadAction<string>
    ) => {
      state.cycleDocuments.isSuccess = false;
      state.cycleDocuments.isLoading = false;
    },

    updatePatientVisitRequest: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.updatePatientVisit.isLoading = true;
      state.updatePatientVisit.isSuccess = null;
    },
    updatePatientVisitSuccess: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.updatePatientVisit.isLoading = false;
      state.updatePatientVisit.isSuccess = true;
      state.updatePatientVisit.data = action.payload;
    },
    updatePatientVisitFailed: (
      state: journey.JourneyStateType,
      { payload: error }: PayloadAction<any>
    ) => {
      state.updatePatientVisit.isLoading = false;
      state.updatePatientVisit.isSuccess = false;
    },

    createFirstCycleRequest: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.createCycle.isLoading = true;
    },
    createFirstCycleSuccess: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.createCycle.isLoading = false;
      state.createCycle.data = action.payload;
      state.createCycle.isSuccess = true;
    },
    createFirstCycleFailed: (
      state: journey.JourneyStateType,
      { payload: error }: PayloadAction<any>
    ) => {
      state.createCycle.isLoading = false;
      state.createCycle.isSuccess = false;
      state.createCycle.errors = error;
    },

    getDrugsRequest: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.getDrugs.isLoading = true;
    },
    getDrugsSuccess: (
      state: journey.JourneyStateType,
      action: PayloadAction<any>
    ) => {
      state.getDrugs.isLoading = false;
      state.getDrugs.data = action.payload;
      state.getDrugs.isSuccess = true;
    },
    getDrugsFailed: (
      state: journey.JourneyStateType,
      { payload: error }: PayloadAction<any>
    ) => {
      state.getDrugs.isLoading = false;
      state.getDrugs.isSuccess = false;
    },
  },
});

export const {
  getCyclesRequest,
  getCyclesSuccess,
  getCyclesError,

  markAttendanceRequest,
  markAttendanceSuccess,
  markAttendanceFailed,

  rescheduleCycleRequest,
  rescheduleCycleSuccess,
  rescheduleCycleFailed,

  getCycleDocumentsRequest,
  getCycleDocumentsSuccess,
  getCycleDocumentsError,

  updatePatientVisitRequest,
  updatePatientVisitSuccess,
  updatePatientVisitFailed,

  createFirstCycleRequest,
  createFirstCycleSuccess,
  createFirstCycleFailed,

  getDrugsRequest,
  getDrugsSuccess,
  getDrugsFailed,
} = JourneySlice.actions;

export default JourneySlice.reducer;
